/**
 * Renders ```mermaid fences as diagrams when the cursor is outside the
 * block. The mermaid library (~1MB) is lazy-loaded on first use only, so
 * notes without diagrams never pay for it.
 */
import { EditorState, StateField, RangeSet } from "@codemirror/state";
import { Decoration, type DecorationSet, EditorView, WidgetType } from "@codemirror/view";
import { syntaxTree } from "@codemirror/language";

let mermaidReady: Promise<typeof import("mermaid").default> | null = null;
function loadMermaid() {
  if (!mermaidReady) {
    mermaidReady = import("mermaid").then((m) => {
      m.default.initialize({
        startOnLoad: false,
        theme: "dark",
        darkMode: true,
        securityLevel: "strict",
        fontFamily: "var(--font-ui)",
      });
      return m.default;
    });
  }
  return mermaidReady;
}

let renderSeq = 0;

class MermaidWidget extends WidgetType {
  constructor(readonly code: string) {
    super();
  }
  override eq(other: MermaidWidget) {
    return other.code === this.code;
  }
  override get estimatedHeight() {
    return 220;
  }
  toDOM() {
    const wrap = document.createElement("div");
    wrap.className = "hn-mermaid";
    wrap.textContent = "rendering diagram…";
    const id = `hn-mmd-${++renderSeq}`;
    void loadMermaid()
      .then(async (mermaid) => {
        const { svg } = await mermaid.render(id, this.code);
        wrap.innerHTML = svg;
      })
      .catch((err: Error) => {
        wrap.textContent = `diagram error: ${err.message.split("\n")[0]}`;
        wrap.classList.add("hn-mermaid-error");
        // mermaid leaves a dangling temp element on parse failure
        document.getElementById(`d${id}`)?.remove();
      });
    return wrap;
  }
  override ignoreEvent() {
    return true;
  }
}

function buildMermaidDecorations(state: EditorState): DecorationSet {
  const decos: ReturnType<Decoration["range"]>[] = [];
  syntaxTree(state).iterate({
    enter: (node) => {
      if (node.name !== "FencedCode") return;
      const info = node.node.getChild("CodeInfo");
      if (!info || state.sliceDoc(info.from, info.to).trim() !== "mermaid") return;
      const touched = state.selection.ranges.some((r) => r.from <= node.to && r.to >= node.from);
      if (touched) return false;
      const text = node.node.getChild("CodeText");
      const code = text ? state.sliceDoc(text.from, text.to) : "";
      if (!code.trim()) return false;
      decos.push(Decoration.replace({ widget: new MermaidWidget(code), block: true }).range(node.from, node.to));
      return false;
    },
  });
  return Decoration.set(decos, true);
}

export const mermaidField = StateField.define<DecorationSet>({
  create: buildMermaidDecorations,
  update(value, tr) {
    if (tr.docChanged || tr.selection || syntaxTree(tr.state) !== syntaxTree(tr.startState)) {
      return buildMermaidDecorations(tr.state);
    }
    return value;
  },
  provide: (f) => [
    EditorView.decorations.from(f),
    EditorView.atomicRanges.of((view) => view.state.field(f, false) ?? RangeSet.empty),
  ],
});

export const mermaidStyles = EditorView.theme({
  ".hn-mermaid": {
    display: "block",
    background: "var(--surface-panel)",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-md)",
    padding: "16px",
    margin: "6px 0",
    textAlign: "center",
    color: "var(--text-muted)",
    fontSize: "0.85em",
    overflowX: "auto",
  },
  ".hn-mermaid svg": {
    maxWidth: "100%",
  },
  ".hn-mermaid-error": {
    color: "var(--danger)",
    fontFamily: "var(--font-mono)",
    textAlign: "left",
  },
});
